import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { GET_LEAVE_TYPES, GET_VACATION_STATS } from '../../graphql/queries';

const countWorkingDays = (start: string, end: string): number => {
  if (!start || !end) return 0;
  const startD = new Date(`${start}T00:00:00Z`);
  const endD = new Date(`${end}T00:00:00Z`);
  if (Number.isNaN(startD.getTime()) || Number.isNaN(endD.getTime())) return 0;
  if (endD < startD) return 0;
  let count = 0;
  const current = new Date(startD);
  while (current <= endD) {
    const dow = current.getUTCDay();
    if (dow !== 0 && dow !== 6) count++;
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return count;
};

const plDays = (n: number) => (n === 1 ? 'dzień' : 'dni');

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  department?: string;
  hireDate?: string;
  terminationDate?: string | null;
  isActive?: boolean;
}

interface LeaveDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: LeaveFormData) => Promise<void>;
  employees: Employee[];
  prefilledStartDate?: string;
  selfServiceMode?: boolean;
  currentEmployeeId?: string;
}

export interface LeaveFormData {
  employeeId: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  reason: string;
}

export const LeaveDialog: React.FC<LeaveDialogProps> = ({
  open,
  onClose,
  onSave,
  employees,
  prefilledStartDate,
  selfServiceMode = false,
  currentEmployeeId,
}) => {
  const { data: leaveTypesData } = useQuery(GET_LEAVE_TYPES);

  const dynamicLeaveTypes = (leaveTypesData?.leaveTypes || [])
    .filter((t: any) => t.isActive)
    .map((t: any) => ({ value: t.code || t.value, label: t.displayName }));

  const defaultLeaveType =
    dynamicLeaveTypes.find((t: any) => t.value === 'VACATION')?.value ||
    dynamicLeaveTypes[0]?.value ||
    'VACATION';

  const [formData, setFormData] = useState<LeaveFormData>({
    employeeId: '',
    leaveType: defaultLeaveType,
    startDate: '',
    endDate: '',
    reason: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveEmployeeId =
    selfServiceMode && currentEmployeeId ? currentEmployeeId : formData.employeeId;
  const selectedEmployee = employees.find((e) => e.id === effectiveEmployeeId);

  const hireDateOnly = selectedEmployee?.hireDate
    ? selectedEmployee.hireDate.split('T')[0]
    : undefined;
  const terminationDateOnly = selectedEmployee?.terminationDate
    ? selectedEmployee.terminationDate.split('T')[0]
    : undefined;

  const formatPlDate = (iso?: string) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
  };

  const datesOutsideWindow = (() => {
    if (!selectedEmployee) return false;
    const checks: string[] = [formData.startDate, formData.endDate].filter(Boolean);
    return checks.some(
      (d) => (hireDateOnly && d < hireDateOnly) || (terminationDateOnly && d > terminationDateOnly),
    );
  })();

  const isVacation = formData.leaveType === 'VACATION';
  const vacationYear = formData.startDate
    ? new Date(`${formData.startDate}T00:00:00Z`).getUTCFullYear()
    : new Date().getUTCFullYear();

  const { data: vacationStatsData } = useQuery(GET_VACATION_STATS, {
    variables: { employeeId: effectiveEmployeeId, year: vacationYear },
    skip: !isVacation || !effectiveEmployeeId,
    fetchPolicy: 'cache-and-network',
  });

  const vacationStats = vacationStatsData?.vacationStats;
  const requestedDays =
    isVacation && formData.startDate && formData.endDate
      ? countWorkingDays(formData.startDate, formData.endDate)
      : 0;
  const exceedsVacationLimit =
    isVacation &&
    !!vacationStats &&
    requestedDays > 0 &&
    requestedDays > vacationStats.availableDays;
  const overBy = exceedsVacationLimit
    ? requestedDays - (vacationStats?.availableDays ?? 0)
    : 0;

  useEffect(() => {
    if (open) {
      setFormData({
        employeeId: selfServiceMode && currentEmployeeId ? currentEmployeeId : '',
        leaveType: defaultLeaveType,
        startDate: prefilledStartDate || '',
        endDate: prefilledStartDate || '',
        reason: '',
      });
      setError(null);
    }
  }, [open, prefilledStartDate, selfServiceMode, currentEmployeeId, defaultLeaveType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!effectiveEmployeeId || !formData.startDate || !formData.endDate) {
      setError('Wypełnij wszystkie wymagane pola');
      return;
    }
    formData.employeeId = effectiveEmployeeId;

    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Błąd podczas zapisywania nieobecności');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{selfServiceMode ? 'Złóż wniosek o nieobecność' : 'Dodaj nową nieobecność'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="p-3 text-sm text-red-600 bg-red-50 rounded-md">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {!selfServiceMode && (
                <div className="space-y-2">
                  <Label htmlFor="employee">
                    Pracownik <span className="text-red-500">*</span>
                  </Label>
                  <select
                    id="employee"
                    className="w-full p-2 border border-input rounded-md bg-background"
                    value={formData.employeeId}
                    onChange={(e) =>
                      setFormData({ ...formData, employeeId: e.target.value })
                    }
                    required
                  >
                    <option value="">Wybierz pracownika</option>
                    {[...employees]
                      .sort((a, b) =>
                        a.lastName.localeCompare(b.lastName, 'pl') ||
                        a.firstName.localeCompare(b.firstName, 'pl')
                      )
                      .map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.lastName} {emp.firstName}
                        {emp.department ? ` - ${emp.department}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="leaveType">Rodzaj</Label>
                <select
                  id="leaveType"
                  className="w-full p-2 border border-input rounded-md bg-background"
                  value={formData.leaveType}
                  onChange={(e) =>
                    setFormData({ ...formData, leaveType: e.target.value })
                  }
                >
                  {dynamicLeaveTypes.map((type: { value: string; label: string }) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="startDate">
                  Data początkowa <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  min={hireDateOnly}
                  max={terminationDateOnly}
                  onChange={(e) =>
                    setFormData({ ...formData, startDate: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="endDate">
                  Data końcowa <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  min={hireDateOnly || formData.startDate || undefined}
                  max={terminationDateOnly}
                  onChange={(e) =>
                    setFormData({ ...formData, endDate: e.target.value })
                  }
                  required
                />
              </div>

              {selectedEmployee && (hireDateOnly || terminationDateOnly) && (
                <div
                  className={`md:col-span-2 text-sm p-2 rounded border ${
                    datesOutsideWindow
                      ? 'bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-200'
                      : 'bg-muted/50 border-border text-muted-foreground'
                  }`}
                >
                  Okres zatrudnienia: {hireDateOnly ? `od ${formatPlDate(hireDateOnly)}` : 'b.d.'}
                  {terminationDateOnly ? ` do ${formatPlDate(terminationDateOnly)}` : ''}
                  {datesOutsideWindow && (
                    <div className="mt-1 font-medium">
                      ⚠ Wybrane daty wykraczają poza okres zatrudnienia. Tylko administrator może to zapisać.
                    </div>
                  )}
                </div>
              )}

              {exceedsVacationLimit && vacationStats && (
                <div className="md:col-span-2 text-sm p-2 rounded border bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-200">
                  <div className="font-medium">
                    ⚠ Wniosek przekracza dostępny limit urlopu o {overBy} {plDays(overBy)}.
                  </div>
                  <div className="mt-1">
                    Limit roczny: {vacationStats.totalDays} · Wykorzystane: {vacationStats.usedDays}
                    {vacationStats.pendingDays > 0 ? ` · Oczekujące: ${vacationStats.pendingDays}` : ''}
                    {' · '}Dostępne: {vacationStats.availableDays} · Wnioskowane: {requestedDays} {plDays(requestedDays)}.
                  </div>
                  <div className="mt-1">
                    Tylko administrator lub kierownik może to zapisać (np. jako zaległy urlop z poprzedniego roku).
                  </div>
                </div>
              )}

              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="reason">Powód (opcjonalnie)</Label>
                <Input
                  id="reason"
                  placeholder="Powód nieobecności..."
                  value={formData.reason}
                  onChange={(e) =>
                    setFormData({ ...formData, reason: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              Anuluj
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Zapisywanie...' : (selfServiceMode ? 'Złóż wniosek' : 'Dodaj nieobecność')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
